// A20-P1.1 — Visual Calendar Views (Day / Week / Month) + helpers.
// Single consolidated file to keep MyCalendar.jsx focused on data
// fetching. All three views are presentational; the parent owns the
// event list and click handler.
import React from "react";
import { useLang } from "@/i18n";

// ─────────────────────────────────────────────────────────────────
// Color enum (13 values from A20-P1.1 model). Tailwind classes are
// pinned so the visual is deterministic across themes.
// ─────────────────────────────────────────────────────────────────
export const COLOR_KEYS = [
  "slate", "emerald", "violet", "sky", "amber", "rose",
  "indigo", "turquoise", "yellow", "orange", "pink", "red", "gray",
];

export const COLOR_CLASS = {
  slate:     { bg: "bg-slate-100",   text: "text-slate-800",   dot: "bg-slate-500" },
  emerald:   { bg: "bg-emerald-100", text: "text-emerald-900", dot: "bg-emerald-500" },
  violet:    { bg: "bg-violet-100",  text: "text-violet-900",  dot: "bg-violet-500" },
  sky:       { bg: "bg-sky-100",     text: "text-sky-900",     dot: "bg-sky-500" },
  amber:     { bg: "bg-amber-100",   text: "text-amber-900",   dot: "bg-amber-500" },
  rose:      { bg: "bg-rose-100",    text: "text-rose-900",    dot: "bg-rose-500" },
  indigo:    { bg: "bg-indigo-100",  text: "text-indigo-900",  dot: "bg-indigo-500" },
  turquoise: { bg: "bg-teal-100",    text: "text-teal-900",    dot: "bg-teal-500" },
  yellow:    { bg: "bg-yellow-100",  text: "text-yellow-900",  dot: "bg-yellow-500" },
  orange:    { bg: "bg-orange-100",  text: "text-orange-900",  dot: "bg-orange-500" },
  pink:      { bg: "bg-pink-100",    text: "text-pink-900",    dot: "bg-pink-500" },
  red:       { bg: "bg-red-100",     text: "text-red-900",     dot: "bg-red-500" },
  gray:      { bg: "bg-zinc-100",    text: "text-zinc-800",    dot: "bg-zinc-500" },
};

const CATEGORY_TO_COLOR = {
  training: "emerald", match: "rose", travel: "sky", meeting: "amber",
  deadline: "red", personal: "violet", tournament: "rose", event: "amber",
  institutional_event: "indigo", internal_task: "gray",
  campaign_followup: "turquoise", institutional_demo: "pink",
  sponsor_meeting: "yellow", other: "slate",
};

export const ALL_CATEGORIES = [
  "training", "match", "travel", "meeting", "deadline", "personal",
  "tournament", "event", "institutional_event",
  "internal_task", "campaign_followup",
  "institutional_demo", "sponsor_meeting", "other",
];

export const colorFor = (ev) => {
  if (ev.color_key && COLOR_CLASS[ev.color_key]) return ev.color_key;
  if (ev.color && COLOR_CLASS[ev.color]) return ev.color;
  const cats = ev.categories || (ev.category ? [ev.category] : []);
  for (const c of cats) {
    if (CATEGORY_TO_COLOR[c]) return CATEGORY_TO_COLOR[c];
  }
  return "emerald";
};

// ─────────────────────────────────────────────────────────────────
// Date helpers (timezone: user's browser TZ; no TZ selector in P1.1).
// ─────────────────────────────────────────────────────────────────
const startOfDay = (d) => { const n = new Date(d); n.setHours(0,0,0,0); return n; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
export const startOfWeek = (d) => {
  // Monday-first week (matches the planning doc).
  const x = startOfDay(d);
  const day = x.getDay();                  // 0=Sun, 1=Mon
  const offset = (day === 0 ? -6 : 1 - day);
  return addDays(x, offset);
};
export const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; };
export const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const fmtMonthYear = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
const fmtDayShort = (d) => d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" });

const inSameDay = (ev, day) => {
  if (!ev.starts_at || !ev.ends_at) return false;
  try {
    const s = new Date(ev.starts_at);
    const e = new Date(ev.ends_at);
    const dStart = startOfDay(day);
    const dEnd = addDays(dStart, 1);
    return e > dStart && s < dEnd;
  } catch { return false; }
};

// Filter helpers ───────────────────────────────────────────────────
export const eventMatchesCategories = (ev, selected) => {
  if (!selected || selected.size === 0) return true;
  const cats = new Set(ev.categories || []);
  if (ev.category) cats.add(ev.category);
  for (const c of cats) if (selected.has(c)) return true;
  return false;
};

// ─────────────────────────────────────────────────────────────────
// Toolbar
// ─────────────────────────────────────────────────────────────────
export const CalendarToolbar = ({ viewMode, setViewMode, anchorDate, setAnchorDate }) => {
  const { t } = useLang();
  const go = (delta) => {
    if (viewMode === "day") setAnchorDate(addDays(anchorDate, delta));
    else if (viewMode === "week") setAnchorDate(addDays(anchorDate, delta * 7));
    else { const x = new Date(anchorDate); x.setMonth(x.getMonth() + delta); setAnchorDate(x); }
  };
  const label = (() => {
    if (viewMode === "month") return fmtMonthYear(anchorDate);
    if (viewMode === "week") {
      const s = startOfWeek(anchorDate);
      const e = addDays(s, 6);
      return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return anchorDate.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  })();
  const Btn = ({ children, active, onClick, testid }) => (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${active ? "bg-emerald-700 text-white" : "bg-white text-slate-700 hover:bg-slate-50 border"}`}
      data-testid={testid}
    >{children}</button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2 justify-between" data-testid="my-calendar-toolbar">
      <div className="flex items-center gap-1">
        <Btn active={viewMode === "day"} onClick={() => setViewMode("day")} testid="my-calendar-view-day">{t("calendar.views.day")}</Btn>
        <Btn active={viewMode === "week"} onClick={() => setViewMode("week")} testid="my-calendar-view-week">{t("calendar.views.week")}</Btn>
        <Btn active={viewMode === "month"} onClick={() => setViewMode("month")} testid="my-calendar-view-month">{t("calendar.views.month")}</Btn>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => go(-1)} className="px-2 py-1 text-xs rounded-md border hover:bg-slate-50" data-testid="my-calendar-nav-prev" aria-label={t("calendar.nav.prev")}><span className="inline-block rtl:rotate-180">◀</span></button>
        <button onClick={() => setAnchorDate(new Date())} className="px-3 py-1 text-xs rounded-md border hover:bg-slate-50" data-testid="my-calendar-nav-today">{t("calendar.nav.today")}</button>
        <button onClick={() => go(1)} className="px-2 py-1 text-xs rounded-md border hover:bg-slate-50" data-testid="my-calendar-nav-next" aria-label={t("calendar.nav.next")}><span className="inline-block rtl:rotate-180">▶</span></button>
      </div>
      <div className="text-sm font-semibold text-slate-700 min-w-[12rem] text-end" data-testid="my-calendar-anchor-label">
        {label}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Multi-category filter (popover via <details>)
// ─────────────────────────────────────────────────────────────────
export const CategoryMultiSelect = ({ selected, setSelected }) => {
  const { t } = useLang();
  const toggle = (c) => {
    const next = new Set(selected);
    if (next.has(c)) next.delete(c); else next.add(c);
    setSelected(next);
  };
  const summary = selected.size === 0
    ? t("calendar.filters.allCategories")
    : t("calendar.filters.selectedN").replace("{n}", String(selected.size));
  return (
    <details className="relative inline-block" data-testid="my-calendar-cat-popover">
      <summary className="cursor-pointer list-none px-3 py-1.5 text-xs font-medium rounded-md border bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2">
        <span>{summary}</span>
        <span className="text-[10px] text-slate-400">▼</span>
      </summary>
      <div className="absolute z-30 mt-1 w-64 bg-white border rounded-md shadow-lg p-3 right-0 max-h-80 overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-700">{t("calendar.filters.selectCategories")}</span>
          {selected.size > 0 && (
            <button
              onClick={(e) => { e.preventDefault(); setSelected(new Set()); }}
              className="text-[11px] text-emerald-700 hover:underline"
              data-testid="my-calendar-cat-clear"
            >{t("calendar.filters.clear")}</button>
          )}
        </div>
        <ul className="space-y-1">
          {ALL_CATEGORIES.map((c) => {
            const checked = selected.has(c);
            const colorKey = CATEGORY_TO_COLOR[c] || "slate";
            const dotClass = COLOR_CLASS[colorKey].dot;
            return (
              <li key={c}>
                <label className="flex items-center gap-2 text-sm py-1 hover:bg-slate-50 px-1 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(c)}
                    className="rounded text-emerald-600"
                    data-testid={`my-calendar-cat-opt-${c}`}
                  />
                  <span className={`w-2 h-2 rounded-full ${dotClass}`} />
                  <span className="text-slate-700">{t(`calendar.categories.${c}`)}</span>
                </label>
              </li>
            );
          })}
        </ul>
        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500">
            {t("calendar.filters.multiSelectHint")}
          </span>
          <button
            type="button"
            onClick={(e) => {
              // Close the <details> popover. Walk up to find the parent.
              const det = e.currentTarget.closest("details");
              if (det) det.removeAttribute("open");
            }}
            className="text-[11px] font-medium px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="my-calendar-cat-apply-close"
          >
            {t("calendar.filters.applyAndClose")}
          </button>
        </div>
      </div>
    </details>
  );
};

// ─────────────────────────────────────────────────────────────────
// Event chip (shared by all 3 views)
// ─────────────────────────────────────────────────────────────────
const EventChip = ({ ev, onClick, dense = false }) => {
  const { t } = useLang();
  const k = colorFor(ev);
  const cls = COLOR_CLASS[k];
  return (
    <button
      onClick={() => onClick && onClick(ev)}
      className={`block w-full text-start ${cls.bg} ${cls.text} rounded px-2 py-1 hover:ring-1 hover:ring-emerald-300 transition ${dense ? "text-[10px] leading-tight truncate" : "text-xs"}`}
      title={ev.title}
      data-testid={`my-calendar-event-chip-${ev.id}`}
    >
      {!dense && (
        <span className="block font-mono text-[10px] text-slate-700/80">{fmtTime(ev.starts_at)}</span>
      )}
      <span className={dense ? "truncate" : "block font-semibold truncate"}>{ev.title}</span>
      {!dense && ev.categories && ev.categories.length > 0 && (
        <span className="block text-[10px] text-slate-600/80 truncate">
          {ev.categories.map((c) => t(`calendar.categories.${c}`)).join(" · ")}
          {ev.other_category_text ? ` (${ev.other_category_text})` : ""}
        </span>
      )}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────
// Day view
// ─────────────────────────────────────────────────────────────────
export const DayView = ({ anchorDate, events, onEventClick }) => {
  const { t } = useLang();
  const dayEvents = events
    .filter((e) => inSameDay(e, anchorDate))
    .sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""));
  if (dayEvents.length === 0) {
    return <div className="text-center text-sm text-slate-500 py-10" data-testid="my-calendar-day-empty">{t("calendar.views.emptyState")}</div>;
  }
  return (
    <ul className="space-y-2" data-testid="my-calendar-day-view">
      {dayEvents.map((ev) => (
        <li key={ev.id}><EventChip ev={ev} onClick={onEventClick} /></li>
      ))}
    </ul>
  );
};

// ─────────────────────────────────────────────────────────────────
// Week view (7 columns; events stacked per day)
// ─────────────────────────────────────────────────────────────────
export const WeekView = ({ anchorDate, events, onEventClick, onDaySelect }) => {
  const { t } = useLang();
  const ws = startOfWeek(anchorDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  return (
    <div className="grid grid-cols-7 gap-2" data-testid="my-calendar-week-view">
      {days.map((d, idx) => {
        const dayEvents = events.filter((e) => inSameDay(e, d));
        const isToday = sameDay(d, new Date());
        return (
          <div
            key={idx}
            className={`border rounded-md min-h-[120px] p-2 ${isToday ? "bg-emerald-50 border-emerald-300" : "bg-white"}`}
            data-testid={`my-calendar-week-day-${idx}`}
            onDoubleClick={() => onDaySelect && onDaySelect(d)}
          >
            <div className={`text-[11px] font-semibold mb-1 ${isToday ? "text-emerald-800" : "text-slate-600"}`}>
              {fmtDayShort(d)}
            </div>
            <ul className="space-y-1">
              {dayEvents.slice(0, 4).map((ev) => (
                <li key={ev.id}><EventChip ev={ev} onClick={onEventClick} dense /></li>
              ))}
              {dayEvents.length > 4 && (
                <li className="text-[10px] text-slate-500">
                  {t("calendar.views.moreN").replace("{n}", String(dayEvents.length - 4))}
                </li>
              )}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Month view (6×7 grid)
// ─────────────────────────────────────────────────────────────────
export const MonthView = ({ anchorDate, events, onEventClick, onDaySelect }) => {
  const { t } = useLang();
  const ms = startOfMonth(anchorDate);
  const gridStart = startOfWeek(ms);
  // 6 weeks × 7 days = 42 cells (covers every month).
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = new Date();
  return (
    <div className="grid grid-cols-7 gap-1 text-xs" data-testid="my-calendar-month-view">
      {cells.map((d, idx) => {
        const isCurrentMonth = d.getMonth() === ms.getMonth();
        const isToday = sameDay(d, today);
        const dayEvents = events.filter((e) => inSameDay(e, d));
        const dayLabel = d.getDate();
        return (
          <button
            key={idx}
            onClick={() => onDaySelect && onDaySelect(d)}
            className={`text-start border rounded p-1 min-h-[88px] transition hover:bg-slate-50 ${isCurrentMonth ? "bg-white" : "bg-slate-50 text-slate-400"} ${isToday ? "ring-1 ring-emerald-400" : ""}`}
            data-testid={`my-calendar-month-cell-${idx}`}
            aria-label={t("calendar.views.openDay")}
          >
            <div className={`text-[11px] font-semibold mb-0.5 ${isToday ? "text-emerald-700" : ""}`}>{dayLabel}</div>
            <ul className="space-y-0.5">
              {dayEvents.slice(0, 3).map((ev) => {
                const k = colorFor(ev);
                const cls = COLOR_CLASS[k];
                return (
                  <li key={ev.id}>
                    <span className={`block ${cls.bg} ${cls.text} rounded px-1 truncate text-[10px]`} title={ev.title}>
                      {ev.title}
                    </span>
                  </li>
                );
              })}
              {dayEvents.length > 3 && (
                <li className="text-[10px] text-slate-500">
                  {t("calendar.views.moreN").replace("{n}", String(dayEvents.length - 3))}
                </li>
              )}
            </ul>
          </button>
        );
      })}
    </div>
  );
};

export default {
  CalendarToolbar, CategoryMultiSelect, DayView, WeekView, MonthView,
  COLOR_KEYS, COLOR_CLASS, ALL_CATEGORIES, colorFor,
};
